import PropTypes from 'prop-types';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { DEFAULT_BENEFIT_ICON, SERVICE_BENEFIT_ICONS } from '../constants';
import { StyledBenefitsBox, StyledIconSquare } from '../SubscriptionCancelPage.style';

// benefitMessageIds (shopping-ui/product-catalog) preferred; fallback to legacy benefits array.
export function SubscriptionBenefits({ benefits, benefitMessageIds, service }) {
  const captureException = useCaptureException();

  const items = benefitMessageIds?.length ? benefitMessageIds.map((id) => ({ messageId: id })) : (benefits ?? []);

  if (items.length === 0) {
    captureException(
      new ZBError('SubscriptionBenefits: no benefits or benefitMessageIds provided', {
        contextData: {
          details: {
            service: service ?? 'unknown',
            hasBenefits: Boolean(benefits?.length > 0),
            hasBenefitMessageIds: Boolean(benefitMessageIds?.length > 0),
          },
        },
      }),
    );
    return null;
  }

  const benefitIcons = SERVICE_BENEFIT_ICONS[service ?? ''] ?? [];
  const icons = Array.isArray(benefitIcons) ? benefitIcons : [];

  return (
    <StyledBenefitsBox>
      <Stack gap={1}>
        <Text size="xs" weight="bold" uppercase color="orange">
          <FormattedMessage id="dashboard.subscriptionCancel.benefits.title" />
        </Text>
        <Stack gap={2}>
          {items.map((item, index) => {
            const Icon = icons[index] || DEFAULT_BENEFIT_ICON;
            const key = item.messageId ?? item.label ?? item.description ?? index;
            return (
              <Stack gap={1.5} horizontal key={key} alignItems="center">
                <StyledIconSquare>
                  <Icon sx={{ color: 'text.orange', fontSize: 16 }} />
                </StyledIconSquare>
                <Text size="sm" color="secondary">
                  {item.messageId ? <FormattedMessage id={item.messageId} /> : item.description}
                </Text>
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </StyledBenefitsBox>
  );
}

SubscriptionBenefits.propTypes = {
  benefits: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  benefitMessageIds: PropTypes.arrayOf(PropTypes.string),
  service: PropTypes.string,
};
